import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import HeroCarousel from '@/components/feature/HeroCarousel';
import SectionHeader from '@/components/base/SectionHeader';
import VideoCard from '@/components/base/VideoCard';
import CategoryEntry from '@/pages/home/components/CategoryEntry';
import LatestUpdates from '@/pages/home/components/LatestUpdates';
import { movies, tvShows, getFeatured, latestUpdates } from '@/data/videos';
import { useSeo } from '@/hooks/useSeo';

const features = [
  {
    icon: 'ri-layout-grid-line',
    title: '多维分类筛选',
    desc: '按地区、年份与排序方式快速缩小范围，减少翻找时间。',
  },
  {
    icon: 'ri-search-2-line',
    title: '全站关键词搜索',
    desc: '片名、类型、主演均可检索，搜索结果页集中呈现。',
  },
  {
    icon: 'ri-play-circle-line',
    title: '播放器界面演示',
    desc: '详情页提供播放进度、剧集切换等交互演示，不含真实片源。',
  },
];

export default function Home() {
  useSeo({
    title: '麻豆视频-高清视频资源在线播放与影视内容分享',
    description:
      '麻豆视频为用户提供丰富的影视内容和高清视频资源，涵盖电影、电视剧等多类别，支持在线播放与内容浏览，满足多样化影视观看需求。',
    keywords: '麻豆视频,视频播放,影视资源,在线视频,免费影视,高清视频,电影,电视剧',
  });

  const featured = getFeatured();
  const hotMovies = [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, 6);
  const hotTv = [...tvShows].sort((a, b) => b.popularity - a.popularity).slice(0, 6);
  const updates = latestUpdates(6);
  const episodeTotal = tvShows.reduce((sum, item) => sum + (item.episodeCount ?? 0), 0);

  return (
    <SiteLayout>
      <div className="animate-fade-in">
        <div className="mx-auto w-full max-w-[1280px] px-4 pt-5 md:px-8 md:pt-6">
          <HeroCarousel items={featured} />
        </div>

        <section className="mx-auto w-full max-w-[1280px] px-4 pt-8 md:px-8 md:pt-10">
          <SectionHeader
            eyebrow="Browse"
            title="按分类浏览"
            subtitle="先选内容形态，再用筛选条件精确定位想看的那一部。"
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <CategoryEntry
              to="/category/movie"
              title="电影"
              subtitle="剧情长片、类型片与作者电影，按地区与年份筛选"
              image="https://readdy.ai/api/search-image?query=wide%20cinematic%20shot%20of%20a%20desert%20highway%20at%20dusk%20with%20a%20lone%20car%20and%20long%20amber%20shadows%2C%20deep%20navy%20sky%20and%20warm%20light%2C%20minimal%20film%20poster%20background%2C%20no%20text%2C%20high%20detail%20editorial%20photography&width=1200&height=600&seq=md-entry-movie&orientation=landscape"
              count={movies.length}
              icon="ri-film-line"
            />
            <CategoryEntry
              to="/category/tv"
              title="电视剧"
              subtitle="剧集连载、单元故事与长篇叙事，支持剧集切换"
              image="https://readdy.ai/api/search-image?query=wide%20cinematic%20view%20of%20a%20night%20city%20skyline%20with%20warm%20glowing%20windows%20and%20soft%20fog%2C%20deep%20navy%20tones%20with%20amber%20lights%2C%20minimal%20series%20poster%20background%2C%20no%20text%2C%20high%20detail%20editorial%20photography&width=1200&height=600&seq=md-entry-tv&orientation=landscape"
              count={tvShows.length}
              icon="ri-tv-2-line"
            />
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 pt-10 md:px-8 md:pt-12">
          <SectionHeader
            eyebrow="Popular Movies"
            title="热门电影推荐"
            subtitle="按站内热度整理的近期热门长片，评分与演职人员均为演示数据。"
            actionLabel="查看全部电影"
            actionTo="/category/movie"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {hotMovies.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 pt-10 md:px-8 md:pt-12">
          <SectionHeader
            eyebrow="Hot Series"
            title="热播电视剧推荐"
            subtitle="近期更新频繁的剧集，点开详情可切换剧集查看播放界面演示。"
            actionLabel="查看全部电视剧"
            actionTo="/category/tv"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {hotTv.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 pt-10 md:px-8 md:pt-12">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[280px_1fr]">
            <div>
              <SectionHeader
                eyebrow="Updated"
                title="最近更新"
                subtitle="按更新时间排序的影视条目。"
              />
              <Link
                to="/search"
                className="flex cursor-pointer items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-700 transition-colors hover:border-accent-300 hover:text-primary-600"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-compass-3-line text-base text-accent-600" />
                  不知道看什么？去搜索
                </span>
                <i className="ri-arrow-right-line text-base" />
              </Link>
            </div>
            <LatestUpdates items={updates} />
          </div>
        </section>

        <section id="about" className="mx-auto w-full max-w-[1280px] px-4 pt-12 md:px-8 md:pt-14">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr] lg:gap-12">
              <div>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-accent-600">
                  <span className="h-1 w-1 rounded-full bg-accent-500" />
                  About
                </span>
                <h2 className="mt-3 font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
                  关于麻豆视频
                </h2>
                <p className="mt-4 text-sm leading-7 text-foreground-600">
                  麻豆视频是一个面向影视爱好者的内容聚合门户，围绕电影与电视剧提供集中的浏览入口。我们把焦点推荐、分类筛选和影片详情放在同一条路径上，让你从首页看到感兴趣的海报，到进入详情了解剧情与演职人员，只需要很少的点击。
                </p>
                <p className="mt-3 text-sm leading-7 text-foreground-600">
                  站内的影视条目、评分、导演与主演信息均为虚构演示内容，用于展示页面结构与交互逻辑；播放区域为界面演示，不接入真实片源，也不提供下载。
                </p>

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {features.map((feature) => (
                    <div key={feature.title} className="rounded-lg bg-background-100 p-4">
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                        <i className={`${feature.icon} text-lg`} />
                      </span>
                      <h3 className="mt-3 text-sm font-semibold text-foreground-900">{feature.title}</h3>
                      <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{feature.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 self-start">
                <div className="rounded-lg bg-primary-950 p-5 text-background-50">
                  <p className="font-heading text-3xl font-extrabold text-accent-400">{movies.length + tvShows.length}</p>
                  <p className="mt-2 text-xs text-primary-200">演示影视条目</p>
                </div>
                <div className="rounded-lg bg-background-100 p-5">
                  <p className="font-heading text-3xl font-extrabold text-primary-700">{episodeTotal}</p>
                  <p className="mt-2 text-xs text-foreground-500">剧集总集数</p>
                </div>
                <div className="rounded-lg bg-background-100 p-5">
                  <p className="font-heading text-3xl font-extrabold text-primary-700">2</p>
                  <p className="mt-2 text-xs text-foreground-500">内容分类</p>
                </div>
                <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                  <p className="font-heading text-3xl font-extrabold text-accent-700">4</p>
                  <p className="mt-2 text-xs text-foreground-600">筛选与排序维度</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}